import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";
import {
  getListingInquiryForViewer, getOrCreateListingInquiry, addListingInquiryMessage,
  onListingInquiry, onListingInquiryMessages, markListingInquiryRead,
} from "../../helpers/firestoreService";
import { ListingAuthContext } from "../ListingLayout";
import { logAnalyticsEvent } from "../../firebaseConfig";

const SUGGESTED_PROMPTS = [
  "Hi, I’m interested in a tour.",
  "Hi, is this property still available?",
  "Hi, I have a question about this property.",
];

/**
 * Floating bottom-right chat bubble on a public listing page. Signed-out
 * visitors are prompted to sign in; once signed in, it's an ongoing 1:1
 * conversation between that prospect and the listing's PM.
 */
const ListingChatWidget = ({ listingId, user, requestTourToken = 0 }) => {
  const { openSignIn } = useContext(ListingAuthContext);
  const [open, setOpen] = useState(false);
  const [tourMode, setTourMode] = useState(false);
  const [inquiry, setInquiry] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const chatAnalyticsParams = (extra = {}) => ({
    signed_in: !!user?.uid,
    has_existing_inquiry: !!inquiry?.id,
    ...extra,
  });

  useEffect(() => {
    if (!requestTourToken) return;
    logAnalyticsEvent("listing_chat_open", chatAnalyticsParams({ open_source: "request_tour" }));
    setTourMode(true);
    setText("");
    setOpen(true);
  }, [requestTourToken]);

  // Look for an existing conversation as soon as we know who's viewing — not
  // just when opened — so the "new message" badge can show up unprompted.
  // Never creates one here; that only happens on the first send.
  useEffect(() => {
    if (!user?.uid || !listingId) return;
    let cancelled = false;
    getListingInquiryForViewer(listingId, user.uid)
      .then((inq) => { if (!cancelled && inq) setInquiry(inq); })
      .catch((e) => console.error("Load listing inquiry error:", e));
    return () => { cancelled = true; };
  }, [user?.uid, listingId]);

  // Keeps unreadByInquirer live once a conversation exists, whether the
  // widget is open or closed.
  useEffect(() => {
    if (!inquiry?.id) return undefined;
    const unsubscribe = onListingInquiry(inquiry.id, (updated) => { if (updated) setInquiry(updated); });
    return () => unsubscribe();
  }, [inquiry?.id]);

  useEffect(() => {
    if (!open || !inquiry?.id) return undefined;
    const unsubscribe = onListingInquiryMessages(inquiry.id, user.uid, "inquirer", setMessages);
    return () => unsubscribe();
  }, [open, inquiry?.id, user?.uid]);

  // Clears the badge both the moment it's opened and if a reply arrives while it's already open.
  useEffect(() => {
    if (open && inquiry?.id && inquiry.unreadByInquirer) {
      markListingInquiryRead(inquiry.id, "inquirer").catch(() => {});
    }
  }, [open, inquiry?.id, inquiry?.unreadByInquirer]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.style.height = "auto";
    inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
  }, [text, open]);

  const handleSend = async () => {
    const body = text.trim();
    if (!body || !user?.uid) return;
    setSending(true);
    setError("");
    try {
      let inq = inquiry;
      if (!inq?.id) {
        inq = await getOrCreateListingInquiry({
          listingId,
          inquirerUid: user.uid,
          inquirerName: user.displayName || "",
          inquirerEmail: user.email || "",
        });
        setInquiry(inq);
      }
      await addListingInquiryMessage(inq.id, {
        senderUid: user.uid,
        senderRole: "inquirer",
        senderName: user.displayName || user.email || "",
        senderEmail: user.email || "",
        text: body,
      });
      logAnalyticsEvent("listing_chat_message_send", chatAnalyticsParams({ tour_mode: tourMode }));
      setText("");
    } catch (e) {
      console.error("Send listing inquiry message error:", e);
      setError("Message couldn't be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (!listingId) return null;

  const hasNewMessage = !open && !!inquiry?.unreadByInquirer;

  return (
    <div
      style={{
        position: "fixed", right: 20, bottom: 20, zIndex: 1050,
        display: "flex", flexDirection: "column", alignItems: "flex-end",
      }}
    >
      {open && (
        <div
          className="bg-white rounded shadow-lg border mb-2 d-flex flex-column"
          style={{ width: 320, maxWidth: "90vw", height: 420 }}
        >
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-primary text-white rounded-top">
            <span className="fw-semibold small">
              <i className="bx bx-conversation me-1" />
              Message the owner
            </span>
            <button
              type="button"
              className="btn-close btn-close-white"
              aria-label="Close"
              onClick={() => setOpen(false)}
            />
          </div>

          <div className="flex-grow-1 d-flex flex-column p-3" style={{ minHeight: 0 }}>
            {!user ? (
              <div className="d-flex flex-column align-items-center justify-content-center text-center flex-grow-1">
                <i className="bx bx-lock-alt text-muted" style={{ fontSize: "2rem" }} />
                <p className="text-muted small mt-2 mb-3">
                  {tourMode
                    ? "Sign in to request a tour and keep your conversation in one place."
                    : "Sign in to inquire about this unit"}
                </p>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => {
                    logAnalyticsEvent("listing_chat_signin_click", chatAnalyticsParams({ tour_mode: tourMode }));
                    openSignIn();
                  }}
                >
                  Sign In
                </Button>
                {tourMode && (
                  <p className="text-muted small mt-3 mb-0">
                    Prefer phone? Call us at <a href="tel:+17028991226">(702) 899-1226</a>.
                  </p>
                )}
              </div>
            ) : (
              <>
                <div ref={boxRef} className="flex-grow-1 overflow-auto mb-2">
                  {messages.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <p className="small mb-0">Ask a question about this unit — the owner will reply here.</p>
                    </div>
                  ) : (
                    messages.map((m) => {
                      const isSelf = m.senderUid === user.uid;
                      return (
                        <div key={m.id} className={`d-flex mb-2 ${isSelf ? "justify-content-end" : "justify-content-start"}`}>
                          <div
                            className={`rounded px-3 py-2 ${isSelf ? "bg-primary text-white" : "bg-light border"}`}
                            style={{ maxWidth: "85%", fontSize: "0.8rem" }}
                          >
                            <span style={{ whiteSpace: "pre-wrap" }}>{m.text}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Input
                    type="textarea"
                    rows={1}
                    innerRef={inputRef}
                    bsSize="sm"
                    placeholder="Write a message…"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    style={{ resize: "none", overflowY: "auto" }}
                  />
                  <Button color="primary" size="sm" disabled={!text.trim() || sending} onClick={handleSend}>
                    {sending ? <Spinner size="sm" /> : <i className="bx bx-send" />}
                  </Button>
                </div>
                <div className="d-flex flex-column align-items-start gap-1 mt-2">
                  {SUGGESTED_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      className="btn btn-link p-0 text-start small fw-semibold"
                      onClick={() => {
                        logAnalyticsEvent("listing_chat_prompt_click", chatAnalyticsParams({ tour_mode: tourMode }));
                        setText(prompt);
                        requestAnimationFrame(() => inputRef.current?.focus());
                      }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
                {error && <div className="text-danger small mt-1">{error}</div>}
                {tourMode && (
                  <small className="text-muted d-block mt-2">
                    Prefer phone? Call us at <a href="tel:+17028991226">(702) 899-1226</a>.
                  </small>
                )}
                {messages.length > 0 && (
                  <small className="text-muted d-block mt-1" style={{ fontSize: "0.7rem" }}>
                    Responses will appear in this chat. You can leave this page and come back.
                  </small>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {!open && (
        <div
          className={`d-flex align-items-center gap-1 px-3 py-2 rounded-pill shadow-sm mb-2 small fw-semibold ${
            hasNewMessage ? "bg-danger text-white" : "bg-white text-dark border"
          }`}
          style={{ whiteSpace: "nowrap", cursor: "pointer" }}
          onClick={() => {
            logAnalyticsEvent("listing_chat_open", chatAnalyticsParams({
              open_source: hasNewMessage ? "new_message_badge" : "label",
            }));
            setOpen(true);
          }}
        >
          <i className={`bx ${hasNewMessage ? "bxs-bell-ring" : "bx-message-rounded-dots"}`} />
          {hasNewMessage ? "New message" : "Request a Tour or Ask a Question"}
        </div>
      )}

      <button
        type="button"
        className="btn btn-primary rounded-circle shadow-lg d-flex align-items-center justify-content-center"
        style={{ width: 56, height: 56 }}
        onClick={() => {
          setOpen((currentlyOpen) => {
            logAnalyticsEvent(currentlyOpen ? "listing_chat_close" : "listing_chat_open", chatAnalyticsParams({ open_source: "floating_button" }));
            return !currentlyOpen;
          });
        }}
        aria-label="Open chat"
      >
        <i className="bx bx-message-dots" style={{ fontSize: "1.5rem" }} />
      </button>
    </div>
  );
};

export default ListingChatWidget;
